import React from "react";
import { motion } from "framer-motion";
import {
  Home,
  Utensils,
  Car,
  Baby,
  HeartHandshake,
  Stethoscope,
  ArrowRight,
} from "lucide-react";

const services = [
  {
    name: "Maid",
    icon: Home,
    points: ["Cleaning support", "Daily household help", "Part-time or full-time"],
    tone: "indigo",
  },
  {
    name: "Cook",
    icon: Utensils,
    points: ["Home-style meals", "Daily cooking help", "Flexible timings"],
    tone: "orange",
  },
  {
    name: "Driver",
    icon: Car,
    points: ["Personal driver support", "Family travel needs", "Reliable assistance"],
    tone: "sky",
  },
  {
    name: "Nanny",
    icon: Baby,
    points: ["Child care support", "Attentive supervision", "Daily routine help"],
    tone: "pink",
  },
  {
    name: "Caretaker",
    icon: HeartHandshake,
    points: ["Elder support", "Companionship care", "Home assistance"],
    tone: "emerald",
  },
  {
    name: "Nurse",
    icon: Stethoscope,
    points: ["Patient care support", "Home nursing help", "Comfort and attention"],
    tone: "violet",
  },
];

const toneStyles = {
  indigo: {
    line: "from-indigo-500 to-violet-500",
    iconWrap: "from-indigo-50 to-violet-50 border-indigo-100 text-indigo-700",
    pill: "bg-indigo-50 text-indigo-700 border-indigo-100",
    glow: "from-indigo-100/50 via-transparent to-violet-100/40",
  },
  orange: {
    line: "from-orange-400 to-amber-500",
    iconWrap: "from-orange-50 to-amber-50 border-orange-100 text-orange-700",
    pill: "bg-orange-50 text-orange-700 border-orange-100",
    glow: "from-orange-100/50 via-transparent to-amber-100/40",
  },
  sky: {
    line: "from-sky-400 to-cyan-500",
    iconWrap: "from-sky-50 to-cyan-50 border-sky-100 text-sky-700",
    pill: "bg-sky-50 text-sky-700 border-sky-100",
    glow: "from-sky-100/50 via-transparent to-cyan-100/40",
  },
  pink: {
    line: "from-pink-400 to-rose-500",
    iconWrap: "from-pink-50 to-rose-50 border-pink-100 text-pink-700",
    pill: "bg-pink-50 text-pink-700 border-pink-100",
    glow: "from-pink-100/50 via-transparent to-rose-100/40",
  },
  emerald: {
    line: "from-emerald-400 to-teal-500",
    iconWrap: "from-emerald-50 to-teal-50 border-emerald-100 text-emerald-700",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-100",
    glow: "from-emerald-100/50 via-transparent to-teal-100/40",
  },
  violet: {
    line: "from-violet-400 to-purple-500",
    iconWrap: "from-violet-50 to-purple-50 border-violet-100 text-violet-700",
    pill: "bg-violet-50 text-violet-700 border-violet-100",
    glow: "from-violet-100/50 via-transparent to-purple-100/40",
  },
};

const HireServices = () => {
  const whatsappBase =
    "https://wa.me/919665855163?text=Hi%20A2Z%20Consultancy,%20I%20want%20to%20know%20more%20about%20";

  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#f8f5ff_0%,#f5f1ff_42%,#fcf9ff_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_16%,rgba(99,102,241,0.10),transparent_22%),radial-gradient(circle_at_84%_20%,rgba(168,85,247,0.08),transparent_20%),radial-gradient(circle_at_80%_80%,rgba(59,130,246,0.06),transparent_22%)]" />
      <div className="absolute top-[-120px] left-[-80px] w-[260px] h-[260px] bg-purple-300/20 rounded-full blur-3xl" />
      <div className="absolute bottom-[-120px] right-[-80px] w-[280px] h-[280px] bg-indigo-300/20 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="text-center mb-14 md:mb-16"
        >
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-indigo-700 font-medium mb-5 shadow-sm">
            Services for Every Home
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Services We Provide
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Explore the type of support you need for your home and choose the help that feels right for your routine.
          </p>
        </motion.div>

        <div className="space-y-5 md:space-y-6">
          {services.map((service, i) => {
            const Icon = service.icon;
            const tone = toneStyles[service.tone];

            return (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-[30px] border border-white/70 bg-white/78 backdrop-blur-xl shadow-[0_14px_35px_rgba(15,23,42,0.07)]"
              >
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-300 bg-gradient-to-br ${tone.glow} pointer-events-none`}
                />
                <div className={`absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b ${tone.line}`} />

                <div className="relative grid lg:grid-cols-[220px_minmax(0,1fr)_180px] gap-6 items-stretch p-6 md:p-7">
                  {/* Left */}
                  <div className="flex items-center gap-4 lg:gap-5">
                    <div
                      className={`w-16 h-16 md:w-20 md:h-20 rounded-[24px] border bg-gradient-to-br ${tone.iconWrap} flex items-center justify-center shadow-sm shrink-0`}
                    >
                      <Icon className="w-8 h-8 md:w-9 md:h-9" />
                    </div>

                    <div className="min-w-0">
                      <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
                        {service.name}
                      </h3>
                      <span
                        className={`inline-flex px-3 py-1.5 rounded-full text-xs font-medium border ${tone.pill}`}
                      >
                        Home Service
                      </span>
                    </div>
                  </div>

                  {/* Middle */}
                  <div className="grid sm:grid-cols-3 gap-3">
                    {service.points.map((point, idx) => (
                      <div
                        key={idx}
                        className="h-full min-h-[76px] rounded-2xl border border-slate-100 bg-white/70 px-4 py-3 text-sm md:text-[15px] text-gray-600 leading-relaxed shadow-sm flex items-center"
                      >
                        {point}
                      </div>
                    ))}
                  </div>

                  {/* Right */}
                  <div className="flex lg:justify-end items-center">
                    <a
                      href={`${whatsappBase}${encodeURIComponent(service.name)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full lg:w-auto"
                    >
                      <button className="w-full lg:w-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-gray-900 px-5 py-3.5 text-white font-medium hover:gap-3 transition-all duration-300 shadow-md">
                        Enquire Now
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HireServices;